import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import { OpenError, OpenInEditorInput } from "./editor";
import {
  AssetAccessError,
  AssetsRpcSchemas,
  AttachmentCreateUploadUrlInput,
  AttachmentCreateUploadUrlResult,
  AttachmentDeleteInput,
  AttachmentUploadSigningKeyError,
} from "./assets";
import { AuthAccessStreamEvent } from "./auth";
import { UsageReadError, UsageSummary, UsageSummaryInput } from "./usage";
import {
  BulkTransferCancelResult,
  BulkTransferError,
  BulkTransferIdInput,
  BulkTransferReadFileInput,
  BulkTransferStreamEvent,
  BulkTransferWriteBeginInput,
  BulkTransferWriteBeginResult,
  BulkTransferWriteChunkInput,
  BulkTransferWriteChunkResult,
  BulkTransferWriteCommitResult,
} from "./bulkTransfer";
import {
  BrowserCloseSessionInput,
  BrowserClearBrowsingDataInput,
  BrowserClearBrowsingDataResult,
  BrowserCommandInput,
  BrowserCommandResult,
  BrowserDisplayAttachInput,
  BrowserDisplayAttachResult,
  BrowserDisplayIceCandidateInput,
  BrowserError,
  BrowserGetSessionInput,
  BrowserListSessionsInput,
  BrowserOpenSessionInput,
  BrowserSessionSnapshot,
  BrowserSubscribeInput,
  BrowserStreamEvent,
} from "./browser";
import { ComputerError, ComputerInputInput, ComputerInputResult } from "./computerUse";
import {
  DevTunnelError,
  DevTunnelListInput,
  DevTunnelRecord,
  DevTunnelRegisterInput,
  DevTunnelUnregisterInput,
} from "./devTunnel";
import {
  ThreadDevRuntimeAllocatePortInput,
  ThreadDevRuntimeError,
  ThreadDevRuntimeGetInput,
  ThreadDevRuntimeRecord,
  ThreadDevRuntimeReleaseInput,
} from "./threadDevRuntime";
import {
  GitActionProgressEvent,
  GitCheckoutInput,
  GitCheckoutResult,
  GitCommandError,
  GitCreateBranchInput,
  GitCreateBranchResult,
  GitCreateWorktreeInput,
  GitCreateWorktreeResult,
  GitInitInput,
  GitListBranchesInput,
  GitListBranchesResult,
  GitManagerServiceError,
  GitPreparePullRequestThreadInput,
  GitPreparePullRequestThreadResult,
  GitPullInput,
  GitPullRequestRefInput,
  GitPullResult,
  GitRemoveWorktreeInput,
  GitResolvePullRequestResult,
  GitRunStackedActionInput,
  GitStatusInput,
  GitStatusResult,
  GitStatusStreamEvent,
} from "./git";
import { KeybindingsConfigError } from "./keybindings";
import {
  ClientOrchestrationCommand,
  ORCHESTRATION_WS_METHODS,
  OrchestrationDispatchCommandError,
  OrchestrationGetFullThreadDiffError,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationGetSnapshotError,
  OrchestrationGetTurnDiffError,
  OrchestrationGetTurnDiffInput,
  OrchestrationReplayEventsError,
  OrchestrationReplayEventsInput,
  OrchestrationRpcSchemas,
  OrchestrationSearchThreadsError,
  OrchestrationSearchThreadsInput,
} from "./orchestration";
import {
  ProviderUploadFeedbackError,
  ProviderUploadFeedbackInput,
  ProviderUploadFeedbackResult,
} from "./provider";
import {
  ProjectSearchContentsError,
  ProjectSearchContentsInput,
  ProjectSearchContentsResult,
  ProjectSearchEntriesError,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
  ProjectWriteFileError,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
} from "./project";
import {
  ProjectCheckoutDeletionExecuteInput,
  ProjectCheckoutDeletionPreview,
  ProjectCheckoutDeletionPreviewInput,
  ProjectCheckoutDeletionResult,
} from "./projectCheckoutDeletion";
import {
  PullRequestActionInput,
  PullRequestActivity,
  PullRequestCommentInput,
  PullRequestCommentUpdateInput,
  PullRequestDetail,
  PullRequestDiffFileContentsInput,
  PullRequestDiffFileContentsResult,
  PullRequestInvalidateInput,
  PullRequestListInput,
  PullRequestListResult,
  PullRequestListStatsInput,
  PullRequestListStatsResult,
  PullRequestOperationError,
  PullRequestReactionInput,
  PullRequestRef,
  PullRequestReviewerCandidateList,
  PullRequestReviewerRequestInput,
  PullRequestSubmitReviewInput,
  PullRequestThreadCommentsInput,
  PullRequestThreadCommentsResult,
  PullRequestThreadReplyInput,
  PullRequestThreadResolutionInput,
  PullRequestUnavailableError,
  PullRequestUpdateInput,
} from "./pullRequest";
import {
  ProviderAuthSession,
  ProviderAuthDisconnectInput,
  ProviderAuthRuntimeRepairInput,
  ProviderAuthRuntimeRepairResult,
  ProviderAuthSessionInput,
  ProviderAuthStartInput,
  ProviderAuthSubmitCodeInput,
  ProviderCredentialCloudSyncInput,
  ProviderCredentialCloudSyncResult,
} from "./providerAuth";
import {
  TerminalClearRequest,
  TerminalCloseRequest,
  TerminalControlAck,
  TerminalError,
  TerminalEvent,
  TerminalAttachInput,
  TerminalAttachStreamEvent,
  TerminalOpenInput,
  TerminalResizeRequest,
  TerminalRestartRequest,
  TerminalRestartResult,
  TerminalSessionSnapshot,
  TerminalSignalRequest,
  TerminalWriteRequest,
} from "./terminal";
import { FilesystemBrowseError, FilesystemBrowseInput, FilesystemBrowseResult } from "./filesystem";
import { ProjectReadFileError, ProjectReadFileInput, ProjectReadFileResult } from "./projectFile";
import {
  ReviewDiffPreviewError,
  ReviewDiffPreviewInput,
  ReviewDiffPreviewResult,
} from "./reviewDiff";
import {
  CloudRuntimeListError,
  CloudRuntimeListInput,
  CloudRuntimeListResult,
} from "./cloudRuntime";
import { TerminalMetadataStreamEvent } from "./terminalMetadataCompat";
import {
  ProjectListEntriesError,
  ProjectListEntriesInput,
  ProjectListEntriesResult,
} from "./projectsCompat";
import {
  WorkspaceListDirectoryError,
  WorkspaceListDirectoryInput,
  WorkspaceListDirectoryResult,
} from "./workspaceDirectory";
import { SourceControlDiscoveryResult } from "./sourceControlCompat";
import {
  SourceControlCloneRepositoryInput,
  SourceControlCloneRepositoryResult,
  SourceControlRepositoryError,
  SourceControlRepositoryInfo,
  SourceControlRepositoryLookupInput,
} from "./sourceControl";
import { ClientActivityReportInput } from "./clientActivityCompat";
import {
  ServerConfigStreamEvent,
  ServerConfig,
  ServerEnvironmentDisplayNameUpdateInput,
  ServerEnvironmentDisplayNameUpdateResult,
  ServerLifecycleStreamEvent,
  ServerProviderUpdatedPayload,
  ServerTraceDiagnosticsResult,
  ServerRemoveKeybindingInput,
  ServerRemoveKeybindingResult,
  ServerUpsertKeybindingInput,
  ServerUpsertKeybindingResult,
} from "./server";
import { NonNegativeInt } from "./baseSchemas";
import { ServerSettings, ServerSettingsError, ServerSettingsPatch } from "./settings";
import {
  PhaseEvent,
  WorkspaceReadinessError,
  WorkspaceReadinessSubscribeInput,
} from "./workspaceReadiness";
import {
  WorkspaceEnsureScratchInput,
  WorkspaceEnsureScratchResult,
  WorkspaceEnvironmentError,
  WorkspaceEnvironmentPreflightInput,
  WorkspaceEnvironmentPreflightReport,
} from "./workspaceEnvironment";
import { RelayAuthorizationMiddleware, RelayDeliveryMiddleware } from "./relay";
import {
  VcsCreateRefInput,
  VcsCreateRefResult,
  VcsCreateWorktreeInput,
  VcsCreateWorktreeResult,
  VcsInitInput,
  VcsListRefsInput,
  VcsListRefsResult,
  VcsPullInput,
  VcsPullResult,
  VcsSwitchRefInput,
  VcsSwitchRefResult,
  VcsStatusResult,
  VcsStatusStreamEvent,
  VcsUnsupportedRequestError,
} from "./vcsCompat";
import { SessionAuthorizationMiddleware } from "./auth";

export const WS_METHODS = {
  // Project registry methods
  projectsList: "projects.list",
  projectsAdd: "projects.add",
  projectsRemove: "projects.remove",
  projectsSearchContents: "projects.searchContents",
  projectsSearchEntries: "projects.searchEntries",
  projectsListEntries: "projects.listEntries",
  serverDiscoverSourceControl: "server.discoverSourceControl",
  serverReportClientActivity: "server.reportClientActivity",
  sourceControlLookupRepository: "sourceControl.lookupRepository",
  sourceControlCloneRepository: "sourceControl.cloneRepository",
  projectsWriteFile: "projects.writeFile",
  projectCheckoutDeletionPreview: "projectCheckoutDeletion.preview",
  projectCheckoutDeletionExecute: "projectCheckoutDeletion.execute",

  // Workspace directory listing (the Files tree)
  workspaceListDirectory: "workspace.listDirectory",

  // Bounded workspace transfer methods
  workspaceReadFile: "workspace.readFile",
  workspaceBeginWriteFile: "workspace.beginWriteFile",
  workspaceWriteFileChunk: "workspace.writeFileChunk",
  workspaceCommitWriteFile: "workspace.commitWriteFile",
  workspaceCancelWriteFile: "workspace.cancelWriteFile",

  // Shell methods
  shellOpenInEditor: "shell.openInEditor",

  // Git methods
  gitPull: "git.pull",
  gitRefreshStatus: "git.refreshStatus",
  gitRunStackedAction: "git.runStackedAction",
  gitListBranches: "git.listBranches",
  // t3code's client speaks vcs.*; see vcsCompat.ts.
  vcsListRefs: "vcs.listRefs",
  vcsRefreshStatus: "vcs.refreshStatus",
  subscribeVcsStatus: "subscribeVcsStatus",
  vcsPull: "vcs.pull",
  vcsCreateWorktree: "vcs.createWorktree",
  vcsRemoveWorktree: "vcs.removeWorktree",
  vcsCreateRef: "vcs.createRef",
  vcsSwitchRef: "vcs.switchRef",
  vcsInit: "vcs.init",
  gitCreateWorktree: "git.createWorktree",
  gitRemoveWorktree: "git.removeWorktree",
  gitCreateBranch: "git.createBranch",
  gitCheckout: "git.checkout",
  gitInit: "git.init",
  gitResolvePullRequest: "git.resolvePullRequest",
  gitPreparePullRequestThread: "git.preparePullRequestThread",

  // Filesystem methods
  filesystemBrowse: "filesystem.browse",
  projectsReadFile: "projects.readFile",
  reviewGetDiffPreview: "review.getDiffPreview",

  // Cloud runtime methods
  cloudListRuntimes: "cloud.listRuntimes",

  // Terminal methods
  terminalOpen: "terminal.open",
  terminalAttach: "terminal.attach",
  terminalWrite: "terminal.write",
  terminalResize: "terminal.resize",
  terminalSignal: "terminal.signal",
  terminalClear: "terminal.clear",
  terminalRestart: "terminal.restart",
  terminalClose: "terminal.close",

  // Browser preview methods
  browserOpenSession: "browser.openSession",
  browserListSessions: "browser.listSessions",
  browserCloseSession: "browser.closeSession",
  browserGetSession: "browser.getSession",
  browserCommand: "browser.command",
  browserClearBrowsingData: "browser.clearBrowsingData",
  browserAttachDisplay: "browser.attachDisplay",
  browserAddDisplayIceCandidate: "browser.addDisplayIceCandidate",

  computerInput: "computer.input",

  devTunnelRegister: "devTunnel.register",
  devTunnelUnregister: "devTunnel.unregister",
  devTunnelList: "devTunnel.list",

  threadDevRuntimeGet: "threadDevRuntime.get",
  threadDevRuntimeAllocatePort: "threadDevRuntime.allocatePort",
  threadDevRuntimeRelease: "threadDevRuntime.release",

  workspaceEnvironmentPreflight: "workspace.environmentPreflight",
  workspaceEnsureScratch: "workspace.ensureScratch",

  pullRequestsList: "pullRequests.list",
  pullRequestsListStats: "pullRequests.listStats",
  pullRequestsDetail: "pullRequests.detail",
  pullRequestsActivity: "pullRequests.activity",
  pullRequestsThreadComments: "pullRequests.threadComments",
  pullRequestsDiffFileContents: "pullRequests.diffFileContents",
  pullRequestsRunAction: "pullRequests.runAction",
  pullRequestsUpdate: "pullRequests.update",
  pullRequestsComment: "pullRequests.comment",
  pullRequestsUpdateComment: "pullRequests.updateComment",
  pullRequestsSubmitReview: "pullRequests.submitReview",
  pullRequestsReplyToThread: "pullRequests.replyToThread",
  pullRequestsSetThreadResolution: "pullRequests.setThreadResolution",
  pullRequestsSetReaction: "pullRequests.setReaction",
  pullRequestsInvalidate: "pullRequests.invalidate",
  pullRequestsReviewerCandidates: "pullRequests.reviewerCandidates",
  pullRequestsRequestReviewers: "pullRequests.requestReviewers",

  // Server meta
  serverGetConfig: "server.getConfig",
  serverRefreshProviders: "server.refreshProviders",
  serverStartProviderAuth: "server.startProviderAuth",
  serverRepairProviderAuthRuntime: "server.repairProviderAuthRuntime",
  serverDisconnectProviderAuth: "server.disconnectProviderAuth",
  serverGetProviderAuthSession: "server.getProviderAuthSession",
  serverCancelProviderAuth: "server.cancelProviderAuth",
  serverSubmitProviderAuthCode: "server.submitProviderAuthCode",
  serverSyncPersonalCodexToCloud: "server.syncPersonalCodexToCloud",
  serverSyncPersonalClaudeToCloud: "server.syncPersonalClaudeToCloud",
  serverUpsertKeybinding: "server.upsertKeybinding",
  serverRemoveKeybinding: "server.removeKeybinding",
  serverGetSettings: "server.getSettings",
  serverUpdateSettings: "server.updateSettings",
  serverUpdateEnvironmentDisplayName: "server.updateEnvironmentDisplayName",
  serverGetTraceDiagnostics: "server.getTraceDiagnostics",
  // Connection keepalive — SPA calls this on a 4s interval so Effect's
  // client-side Socket scope sees real protocol traffic and stays alive.
  // Without it the WS would close cleanly with code 1000 every ~7s
  // (Effect 4 Socket.makeWebSocket releases ws.close(1000) on scope end).
  // See FORGE-216 and project_forge_ws_idle_close memory note.
  serverHeartbeat: "server.heartbeat",

  // Usage reporting
  usageGetSummary: "usage.getSummary",

  // Streaming subscriptions
  subscribeGitStatus: "subscribeGitStatus",
  subscribeTerminalEvents: "subscribeTerminalEvents",
  subscribeTerminalMetadata: "subscribeTerminalMetadata",
  subscribeBrowserEvents: "subscribeBrowserEvents",
  subscribeServerConfig: "subscribeServerConfig",
  subscribeServerLifecycle: "subscribeServerLifecycle",
  subscribeAuthAccess: "subscribeAuthAccess",
  // Workspace readiness phase stream (FINDING-CB1 long-term fix).
  // SPA subscribes after WS upgrade succeeds and receives typed phase events
  // mirroring the master's WorkspaceReadinessState. Pre-upgrade phase
  // visibility is delivered via the HTTP SSE endpoint /api/workspace/readiness
  // since browsers can't read 503 bodies on failed WS upgrades.
  subscribeWorkspaceReadiness: "subscribeWorkspaceReadiness",

  // Asset addressing. The bytes are already served over HTTP; this is the
  // method that tells a client which URL to fetch them from.
  assetsCreateUrl: "assets.createUrl",
  attachmentsCreateUploadUrl: "attachments.createUploadUrl",
  attachmentsDelete: "attachments.delete",

  // Feedback the person filed from inside a thread.
  providerUploadFeedback: "provider.uploadFeedback",
} as const;

export const WsServerUpsertKeybindingRpc = Rpc.make(WS_METHODS.serverUpsertKeybinding, {
  payload: ServerUpsertKeybindingInput,
  success: ServerUpsertKeybindingResult,
  error: KeybindingsConfigError,
});

export const WsServerRemoveKeybindingRpc = Rpc.make(WS_METHODS.serverRemoveKeybinding, {
  payload: ServerRemoveKeybindingInput,
  success: ServerRemoveKeybindingResult,
  error: KeybindingsConfigError,
});

export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
  payload: Schema.Struct({}),
  success: ServerConfig,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError]),
});

export const WsServerRefreshProvidersRpc = Rpc.make(WS_METHODS.serverRefreshProviders, {
  payload: Schema.Struct({}),
  success: ServerProviderUpdatedPayload,
});

export const WsServerStartProviderAuthRpc = Rpc.make(WS_METHODS.serverStartProviderAuth, {
  payload: ProviderAuthStartInput,
  success: ProviderAuthSession,
});

export const WsServerRepairProviderAuthRuntimeRpc = Rpc.make(
  WS_METHODS.serverRepairProviderAuthRuntime,
  {
    payload: ProviderAuthRuntimeRepairInput,
    success: ProviderAuthRuntimeRepairResult,
  },
);

export const WsServerDisconnectProviderAuthRpc = Rpc.make(WS_METHODS.serverDisconnectProviderAuth, {
  payload: ProviderAuthDisconnectInput,
  success: ServerProviderUpdatedPayload,
});

export const WsServerGetProviderAuthSessionRpc = Rpc.make(WS_METHODS.serverGetProviderAuthSession, {
  payload: ProviderAuthSessionInput,
  success: ProviderAuthSession,
});

export const WsServerCancelProviderAuthRpc = Rpc.make(WS_METHODS.serverCancelProviderAuth, {
  payload: ProviderAuthSessionInput,
  success: ProviderAuthSession,
});

export const WsServerSubmitProviderAuthCodeRpc = Rpc.make(WS_METHODS.serverSubmitProviderAuthCode, {
  payload: ProviderAuthSubmitCodeInput,
  success: ProviderAuthSession,
});

export const WsServerSyncPersonalCodexToCloudRpc = Rpc.make(
  WS_METHODS.serverSyncPersonalCodexToCloud,
  {
    payload: ProviderCredentialCloudSyncInput,
    success: ProviderCredentialCloudSyncResult,
  },
);

export const WsServerSyncPersonalClaudeToCloudRpc = Rpc.make(
  WS_METHODS.serverSyncPersonalClaudeToCloud,
  {
    payload: ProviderCredentialCloudSyncInput,
    success: ProviderCredentialCloudSyncResult,
  },
);

export const WsServerGetSettingsRpc = Rpc.make(WS_METHODS.serverGetSettings, {
  payload: Schema.Struct({}),
  success: ServerSettings,
  error: ServerSettingsError,
});

export const WsServerUpdateSettingsRpc = Rpc.make(WS_METHODS.serverUpdateSettings, {
  payload: Schema.Struct({ patch: ServerSettingsPatch }),
  success: ServerSettings,
  error: ServerSettingsError,
});

export const WsServerUpdateEnvironmentDisplayNameRpc = Rpc.make(
  WS_METHODS.serverUpdateEnvironmentDisplayName,
  {
    payload: ServerEnvironmentDisplayNameUpdateInput,
    success: ServerEnvironmentDisplayNameUpdateResult,
  },
);

export const WsServerTraceDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
  // The threshold is the caller's, not the server's: what counts as slow
  // depends on which surface is asking. Absent means the server's default.
  payload: Schema.Struct({ slowSpanThresholdMs: Schema.optionalKey(NonNegativeInt) }),
  // No error channel. An unreadable or absent trace file is a legitimate
  // answer carried in the result, not a failed request.
  success: ServerTraceDiagnosticsResult,
});

export const WsServerHeartbeatRpc = Rpc.make(WS_METHODS.serverHeartbeat, {
  payload: Schema.Struct({}),
  success: Schema.Struct({}),
});

const PullRequestRpcError = Schema.Union([PullRequestUnavailableError, PullRequestOperationError]);
export const WsPullRequestsListRpc = Rpc.make(WS_METHODS.pullRequestsList, {
  payload: PullRequestListInput,
  success: PullRequestListResult,
  error: PullRequestRpcError,
});
export const WsPullRequestsListStatsRpc = Rpc.make(WS_METHODS.pullRequestsListStats, {
  payload: PullRequestListStatsInput,
  success: PullRequestListStatsResult,
  error: PullRequestRpcError,
});
export const WsPullRequestsDetailRpc = Rpc.make(WS_METHODS.pullRequestsDetail, {
  payload: PullRequestRef,
  success: PullRequestDetail,
  error: PullRequestRpcError,
});
export const WsPullRequestsActivityRpc = Rpc.make(WS_METHODS.pullRequestsActivity, {
  payload: PullRequestRef,
  success: PullRequestActivity,
  error: PullRequestRpcError,
});
export const WsPullRequestsThreadCommentsRpc = Rpc.make(WS_METHODS.pullRequestsThreadComments, {
  payload: PullRequestThreadCommentsInput,
  success: PullRequestThreadCommentsResult,
  error: PullRequestRpcError,
});
export const WsPullRequestsDiffFileContentsRpc = Rpc.make(WS_METHODS.pullRequestsDiffFileContents, {
  payload: PullRequestDiffFileContentsInput,
  success: PullRequestDiffFileContentsResult,
  error: PullRequestRpcError,
});
export const WsPullRequestsRunActionRpc = Rpc.make(WS_METHODS.pullRequestsRunAction, {
  payload: PullRequestActionInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsUpdateRpc = Rpc.make(WS_METHODS.pullRequestsUpdate, {
  payload: PullRequestUpdateInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsCommentRpc = Rpc.make(WS_METHODS.pullRequestsComment, {
  payload: PullRequestCommentInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsUpdateCommentRpc = Rpc.make(WS_METHODS.pullRequestsUpdateComment, {
  payload: PullRequestCommentUpdateInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsSubmitReviewRpc = Rpc.make(WS_METHODS.pullRequestsSubmitReview, {
  payload: PullRequestSubmitReviewInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsReplyToThreadRpc = Rpc.make(WS_METHODS.pullRequestsReplyToThread, {
  payload: PullRequestThreadReplyInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsSetThreadResolutionRpc = Rpc.make(
  WS_METHODS.pullRequestsSetThreadResolution,
  { payload: PullRequestThreadResolutionInput, success: Schema.Void, error: PullRequestRpcError },
);
export const WsPullRequestsSetReactionRpc = Rpc.make(WS_METHODS.pullRequestsSetReaction, {
  payload: PullRequestReactionInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsInvalidateRpc = Rpc.make(WS_METHODS.pullRequestsInvalidate, {
  payload: PullRequestInvalidateInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});
export const WsPullRequestsReviewerCandidatesRpc = Rpc.make(
  WS_METHODS.pullRequestsReviewerCandidates,
  {
    payload: PullRequestRef,
    success: PullRequestReviewerCandidateList,
    error: PullRequestRpcError,
  },
);
export const WsPullRequestsRequestReviewersRpc = Rpc.make(WS_METHODS.pullRequestsRequestReviewers, {
  payload: PullRequestReviewerRequestInput,
  success: Schema.Void,
  error: PullRequestRpcError,
});

export const WsProjectsSearchContentsRpc = Rpc.make(WS_METHODS.projectsSearchContents, {
  payload: ProjectSearchContentsInput,
  success: ProjectSearchContentsResult,
  error: ProjectSearchContentsError,
});

export const WsProjectsSearchEntriesRpc = Rpc.make(WS_METHODS.projectsSearchEntries, {
  payload: ProjectSearchEntriesInput,
  success: ProjectSearchEntriesResult,
  error: ProjectSearchEntriesError,
});

export const WsProjectsWriteFileRpc = Rpc.make(WS_METHODS.projectsWriteFile, {
  payload: ProjectWriteFileInput,
  success: ProjectWriteFileResult,
  error: ProjectWriteFileError,
});

export const WsProjectCheckoutDeletionPreviewRpc = Rpc.make(
  WS_METHODS.projectCheckoutDeletionPreview,
  {
    payload: ProjectCheckoutDeletionPreviewInput,
    success: ProjectCheckoutDeletionPreview,
  },
);

export const WsProjectCheckoutDeletionExecuteRpc = Rpc.make(
  WS_METHODS.projectCheckoutDeletionExecute,
  {
    payload: ProjectCheckoutDeletionExecuteInput,
    success: ProjectCheckoutDeletionResult,
  },
);

export const WsWorkspaceReadFileRpc = Rpc.make(WS_METHODS.workspaceReadFile, {
  payload: BulkTransferReadFileInput,
  success: BulkTransferStreamEvent,
  error: BulkTransferError,
  stream: true,
});

export const WsWorkspaceBeginWriteFileRpc = Rpc.make(WS_METHODS.workspaceBeginWriteFile, {
  payload: BulkTransferWriteBeginInput,
  success: BulkTransferWriteBeginResult,
  error: BulkTransferError,
});

export const WsWorkspaceWriteFileChunkRpc = Rpc.make(WS_METHODS.workspaceWriteFileChunk, {
  payload: BulkTransferWriteChunkInput,
  success: BulkTransferWriteChunkResult,
  error: BulkTransferError,
});

export const WsWorkspaceCommitWriteFileRpc = Rpc.make(WS_METHODS.workspaceCommitWriteFile, {
  payload: BulkTransferIdInput,
  success: BulkTransferWriteCommitResult,
  error: BulkTransferError,
});

export const WsWorkspaceCancelWriteFileRpc = Rpc.make(WS_METHODS.workspaceCancelWriteFile, {
  payload: BulkTransferIdInput,
  success: BulkTransferCancelResult,
  error: BulkTransferError,
});

/**
 * One workspace directory's immediate children, for the right panel's Files
 * tree. Read-scoped and confined to the workspace root on the server; the tree
 * expands one directory at a time through this method rather than listing the
 * whole workspace up front.
 */
export const WsWorkspaceListDirectoryRpc = Rpc.make(WS_METHODS.workspaceListDirectory, {
  payload: WorkspaceListDirectoryInput,
  success: WorkspaceListDirectoryResult,
  error: WorkspaceListDirectoryError,
});

export const WsShellOpenInEditorRpc = Rpc.make(WS_METHODS.shellOpenInEditor, {
  payload: OpenInEditorInput,
  error: OpenError,
});

export const WsSubscribeGitStatusRpc = Rpc.make(WS_METHODS.subscribeGitStatus, {
  payload: GitStatusInput,
  success: GitStatusStreamEvent,
  error: GitManagerServiceError,
  stream: true,
});

export const WsGitPullRpc = Rpc.make(WS_METHODS.gitPull, {
  payload: GitPullInput,
  success: GitPullResult,
  error: GitCommandError,
});

export const WsGitRefreshStatusRpc = Rpc.make(WS_METHODS.gitRefreshStatus, {
  payload: GitStatusInput,
  success: GitStatusResult,
  error: GitManagerServiceError,
});

export const WsGitRunStackedActionRpc = Rpc.make(WS_METHODS.gitRunStackedAction, {
  payload: GitRunStackedActionInput,
  success: GitActionProgressEvent,
  error: GitManagerServiceError,
  stream: true,
});

export const WsGitResolvePullRequestRpc = Rpc.make(WS_METHODS.gitResolvePullRequest, {
  payload: GitPullRequestRefInput,
  success: GitResolvePullRequestResult,
  error: GitManagerServiceError,
});

export const WsGitPreparePullRequestThreadRpc = Rpc.make(WS_METHODS.gitPreparePullRequestThread, {
  payload: GitPreparePullRequestThreadInput,
  success: GitPreparePullRequestThreadResult,
  error: GitManagerServiceError,
});

export const WsGitListBranchesRpc = Rpc.make(WS_METHODS.gitListBranches, {
  payload: GitListBranchesInput,
  success: GitListBranchesResult,
  error: GitCommandError,
});

export const WsVcsListRefsRpc = Rpc.make(WS_METHODS.vcsListRefs, {
  payload: VcsListRefsInput,
  success: VcsListRefsResult,
  error: GitCommandError,
});

export const WsVcsRefreshStatusRpc = Rpc.make(WS_METHODS.vcsRefreshStatus, {
  payload: GitStatusInput,
  success: VcsStatusResult,
  // Mirrors gitRefreshStatus: the broadcaster fails with GitManagerServiceError,
  // not the GitCommandError the one-shot git commands use.
  error: GitManagerServiceError,
});

export const WsSubscribeVcsStatusRpc = Rpc.make(WS_METHODS.subscribeVcsStatus, {
  payload: GitStatusInput,
  success: VcsStatusStreamEvent,
  error: GitManagerServiceError,
  stream: true,
});

export const WsVcsPullRpc = Rpc.make(WS_METHODS.vcsPull, {
  payload: VcsPullInput,
  success: VcsPullResult,
  error: GitCommandError,
});

export const WsVcsCreateWorktreeRpc = Rpc.make(WS_METHODS.vcsCreateWorktree, {
  payload: VcsCreateWorktreeInput,
  success: VcsCreateWorktreeResult,
  error: Schema.Union([GitCommandError, VcsUnsupportedRequestError]),
});

export const WsVcsRemoveWorktreeRpc = Rpc.make(WS_METHODS.vcsRemoveWorktree, {
  payload: GitRemoveWorktreeInput,
  error: GitCommandError,
});

export const WsVcsCreateRefRpc = Rpc.make(WS_METHODS.vcsCreateRef, {
  payload: VcsCreateRefInput,
  success: VcsCreateRefResult,
  error: GitCommandError,
});

export const WsVcsSwitchRefRpc = Rpc.make(WS_METHODS.vcsSwitchRef, {
  payload: VcsSwitchRefInput,
  success: VcsSwitchRefResult,
  error: GitCommandError,
});

export const WsVcsInitRpc = Rpc.make(WS_METHODS.vcsInit, {
  payload: VcsInitInput,
  error: Schema.Union([GitCommandError, VcsUnsupportedRequestError]),
});

export const WsGitCreateWorktreeRpc = Rpc.make(WS_METHODS.gitCreateWorktree, {
  payload: GitCreateWorktreeInput,
  success: GitCreateWorktreeResult,
  error: GitCommandError,
});

export const WsGitRemoveWorktreeRpc = Rpc.make(WS_METHODS.gitRemoveWorktree, {
  payload: GitRemoveWorktreeInput,
  error: GitCommandError,
});

export const WsGitCreateBranchRpc = Rpc.make(WS_METHODS.gitCreateBranch, {
  payload: GitCreateBranchInput,
  success: GitCreateBranchResult,
  error: GitCommandError,
});

export const WsGitCheckoutRpc = Rpc.make(WS_METHODS.gitCheckout, {
  payload: GitCheckoutInput,
  success: GitCheckoutResult,
  error: GitCommandError,
});

export const WsGitInitRpc = Rpc.make(WS_METHODS.gitInit, {
  payload: GitInitInput,
  error: GitCommandError,
});

/**
 * The git half of a review: the branch range and the uncommitted work. The
 * per-turn diffs beside it arrive through `orchestration.getTurnDiff`.
 */
export const WsReviewGetDiffPreviewRpc = Rpc.make(WS_METHODS.reviewGetDiffPreview, {
  payload: ReviewDiffPreviewInput,
  success: ReviewDiffPreviewResult,
  error: Schema.Union([ReviewDiffPreviewError]),
});

/**
 * Reading one file whole, distinct from `workspace.readFile`, which is the
 * chunked bulk-transfer stream. A viewer wants the text once; a transfer wants
 * offsets and checksums.
 */
export const WsProjectsReadFileRpc = Rpc.make(WS_METHODS.projectsReadFile, {
  payload: ProjectReadFileInput,
  success: ProjectReadFileResult,
  error: Schema.Union([ProjectReadFileError]),
});

export const WsFilesystemBrowseRpc = Rpc.make(WS_METHODS.filesystemBrowse, {
  payload: FilesystemBrowseInput,
  success: FilesystemBrowseResult,
  error: Schema.Union([FilesystemBrowseError]),
});

/**
 * The caller's Forge Cloud runtimes, read through CMMD with their own JWT.
 * Routed through the server because the phone keeps only the Forge-minted
 * session token, never a CMMD one.
 */
export const WsCloudListRuntimesRpc = Rpc.make(WS_METHODS.cloudListRuntimes, {
  payload: CloudRuntimeListInput,
  success: CloudRuntimeListResult,
  error: Schema.Union([CloudRuntimeListError]),
});

export const WsTerminalOpenRpc = Rpc.make(WS_METHODS.terminalOpen, {
  payload: TerminalOpenInput,
  success: TerminalSessionSnapshot,
  error: TerminalError,
});

export const WsTerminalAttachRpc = Rpc.make(WS_METHODS.terminalAttach, {
  payload: TerminalAttachInput,
  success: TerminalAttachStreamEvent,
  error: TerminalError,
  stream: true,
});

export const WsTerminalWriteRpc = Rpc.make(WS_METHODS.terminalWrite, {
  payload: TerminalWriteRequest,
  success: TerminalControlAck,
  error: TerminalError,
});

export const WsTerminalResizeRpc = Rpc.make(WS_METHODS.terminalResize, {
  payload: TerminalResizeRequest,
  success: TerminalControlAck,
  error: TerminalError,
});

export const WsTerminalSignalRpc = Rpc.make(WS_METHODS.terminalSignal, {
  payload: TerminalSignalRequest,
  success: TerminalControlAck,
  error: TerminalError,
});

export const WsTerminalClearRpc = Rpc.make(WS_METHODS.terminalClear, {
  payload: TerminalClearRequest,
  success: TerminalControlAck,
  error: TerminalError,
});

export const WsTerminalRestartRpc = Rpc.make(WS_METHODS.terminalRestart, {
  payload: TerminalRestartRequest,
  success: TerminalRestartResult,
  error: TerminalError,
});

export const WsTerminalCloseRpc = Rpc.make(WS_METHODS.terminalClose, {
  payload: TerminalCloseRequest,
  success: TerminalControlAck,
  error: TerminalError,
});

export const WsBrowserOpenSessionRpc = Rpc.make(WS_METHODS.browserOpenSession, {
  payload: BrowserOpenSessionInput,
  success: BrowserSessionSnapshot,
  error: BrowserError,
});

export const WsBrowserListSessionsRpc = Rpc.make(WS_METHODS.browserListSessions, {
  payload: BrowserListSessionsInput,
  success: Schema.Array(BrowserSessionSnapshot),
  error: BrowserError,
});

export const WsBrowserCloseSessionRpc = Rpc.make(WS_METHODS.browserCloseSession, {
  payload: BrowserCloseSessionInput,
  error: BrowserError,
});

export const WsBrowserGetSessionRpc = Rpc.make(WS_METHODS.browserGetSession, {
  payload: BrowserGetSessionInput,
  success: Schema.NullOr(BrowserSessionSnapshot),
  error: BrowserError,
});

export const WsBrowserCommandRpc = Rpc.make(WS_METHODS.browserCommand, {
  payload: BrowserCommandInput,
  success: BrowserCommandResult,
  error: BrowserError,
});

export const WsBrowserClearBrowsingDataRpc = Rpc.make(WS_METHODS.browserClearBrowsingData, {
  payload: BrowserClearBrowsingDataInput,
  success: BrowserClearBrowsingDataResult,
  error: BrowserError,
});

export const WsBrowserAttachDisplayRpc = Rpc.make(WS_METHODS.browserAttachDisplay, {
  payload: BrowserDisplayAttachInput,
  success: BrowserDisplayAttachResult,
  error: BrowserError,
});

export const WsBrowserAddDisplayIceCandidateRpc = Rpc.make(
  WS_METHODS.browserAddDisplayIceCandidate,
  {
    payload: BrowserDisplayIceCandidateInput,
    success: Schema.Struct({}),
    error: BrowserError,
  },
);

/**
 * Human input to the runtime desktop.
 *
 * Separate from `browser.command` because that lane is Playwright driving a
 * page: it can click a DOM element but cannot touch the dock, a file manager,
 * or anything else outside Chromium. This one lands on the display itself.
 */
export const WsComputerInputRpc = Rpc.make(WS_METHODS.computerInput, {
  payload: ComputerInputInput,
  success: ComputerInputResult,
  error: ComputerError,
});

export const WsDevTunnelRegisterRpc = Rpc.make(WS_METHODS.devTunnelRegister, {
  payload: DevTunnelRegisterInput,
  success: DevTunnelRecord,
  error: DevTunnelError,
});

export const WsDevTunnelUnregisterRpc = Rpc.make(WS_METHODS.devTunnelUnregister, {
  payload: DevTunnelUnregisterInput,
  error: DevTunnelError,
});

export const WsDevTunnelListRpc = Rpc.make(WS_METHODS.devTunnelList, {
  payload: DevTunnelListInput,
  success: Schema.Array(DevTunnelRecord),
  error: DevTunnelError,
});

export const WsThreadDevRuntimeGetRpc = Rpc.make(WS_METHODS.threadDevRuntimeGet, {
  payload: ThreadDevRuntimeGetInput,
  success: Schema.NullOr(ThreadDevRuntimeRecord),
  error: ThreadDevRuntimeError,
});

export const WsThreadDevRuntimeAllocatePortRpc = Rpc.make(WS_METHODS.threadDevRuntimeAllocatePort, {
  payload: ThreadDevRuntimeAllocatePortInput,
  success: ThreadDevRuntimeRecord,
  error: ThreadDevRuntimeError,
});

export const WsThreadDevRuntimeReleaseRpc = Rpc.make(WS_METHODS.threadDevRuntimeRelease, {
  payload: ThreadDevRuntimeReleaseInput,
  error: ThreadDevRuntimeError,
});

export const WsWorkspaceEnvironmentPreflightRpc = Rpc.make(
  WS_METHODS.workspaceEnvironmentPreflight,
  {
    payload: WorkspaceEnvironmentPreflightInput,
    success: WorkspaceEnvironmentPreflightReport,
    error: WorkspaceEnvironmentError,
  },
);

export const WsWorkspaceEnsureScratchRpc = Rpc.make(WS_METHODS.workspaceEnsureScratch, {
  payload: WorkspaceEnsureScratchInput,
  success: WorkspaceEnsureScratchResult,
  error: WorkspaceEnvironmentError,
});

export const WsOrchestrationDispatchCommandRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.dispatchCommand,
  {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: OrchestrationDispatchCommandError,
  },
);

export const WsOrchestrationGetTurnDiffRpc = Rpc.make(ORCHESTRATION_WS_METHODS.getTurnDiff, {
  payload: OrchestrationGetTurnDiffInput,
  success: OrchestrationRpcSchemas.getTurnDiff.output,
  error: OrchestrationGetTurnDiffError,
});

export const WsOrchestrationGetFullThreadDiffRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getFullThreadDiff,
  {
    payload: OrchestrationGetFullThreadDiffInput,
    success: OrchestrationRpcSchemas.getFullThreadDiff.output,
    error: OrchestrationGetFullThreadDiffError,
  },
);

export const WsOrchestrationStreamTurnDiffRpc = Rpc.make(ORCHESTRATION_WS_METHODS.streamTurnDiff, {
  payload: OrchestrationGetTurnDiffInput,
  success: BulkTransferStreamEvent,
  error: Schema.Union([OrchestrationGetTurnDiffError, BulkTransferError]),
  stream: true,
});

export const WsOrchestrationStreamFullThreadDiffRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.streamFullThreadDiff,
  {
    payload: OrchestrationGetFullThreadDiffInput,
    success: BulkTransferStreamEvent,
    error: Schema.Union([OrchestrationGetFullThreadDiffError, BulkTransferError]),
    stream: true,
  },
);

export const WsOrchestrationReplayEventsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.replayEvents, {
  payload: OrchestrationReplayEventsInput,
  success: OrchestrationRpcSchemas.replayEvents.output,
  error: OrchestrationReplayEventsError,
});

export const WsOrchestrationGetArchivedShellSnapshotRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot,
  {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: OrchestrationGetSnapshotError,
  },
);

export const WsOrchestrationSearchThreadsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.searchThreads, {
  payload: OrchestrationSearchThreadsInput,
  success: OrchestrationRpcSchemas.searchThreads.output,
  error: OrchestrationSearchThreadsError,
});

/**
 * The socket-only path for "load earlier turns": a relay-only connection has
 * no HTTP endpoint, so this reads the same windowed page the HTTP snapshot
 * route and `subscribeThread`'s fallback snapshot serve, over the RPC socket
 * instead.
 */
export const WsOrchestrationGetThreadDetailPageRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getThreadDetailPage,
  {
    payload: OrchestrationRpcSchemas.getThreadDetailPage.input,
    success: OrchestrationRpcSchemas.getThreadDetailPage.output,
    error: OrchestrationGetSnapshotError,
  },
);

/**
 * Resolve `owner/repo` to the repository the caller is allowed to clone.
 *
 * Both of these run against the same CMMD-brokered GitHub credential the web
 * add-project flow uses, so a lookup that answers is a lookup whose clone can
 * also be authorized, and a repository the caller cannot reach is a failure
 * here rather than a clone that dies half way.
 */
export const WsSourceControlLookupRepositoryRpc = Rpc.make(
  WS_METHODS.sourceControlLookupRepository,
  {
    payload: SourceControlRepositoryLookupInput,
    success: SourceControlRepositoryInfo,
    error: SourceControlRepositoryError,
  },
);

export const WsSourceControlCloneRepositoryRpc = Rpc.make(WS_METHODS.sourceControlCloneRepository, {
  payload: SourceControlCloneRepositoryInput,
  success: SourceControlCloneRepositoryResult,
  error: SourceControlRepositoryError,
});

export const WsOrchestrationSubscribeShellRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeShell, {
  payload: OrchestrationRpcSchemas.subscribeShell.input,
  success: OrchestrationRpcSchemas.subscribeShell.output,
  error: OrchestrationGetSnapshotError,
  stream: true,
});

export const WsOrchestrationSubscribeThreadRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.subscribeThread,
  {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: OrchestrationGetSnapshotError,
    stream: true,
  },
);

export const WsSubscribeTerminalEventsRpc = Rpc.make(WS_METHODS.subscribeTerminalEvents, {
  payload: Schema.Struct({}),
  success: TerminalEvent,
  stream: true,
});

/**
 * The terminal list feed the vendored mobile client subscribes to. See
 * packages/contracts/src/terminalMetadataCompat.ts.
 */
/**
 * The unfiltered file listing the vendored mobile client's Files screen opens
 * with. See packages/contracts/src/projectsCompat.ts.
 */
/**
 * What this host can do about version control, which the vendored mobile client
 * asks on every thread open. See packages/contracts/src/sourceControlCompat.ts.
 */
/**
 * The client's activity report, accepted and dropped. See
 * packages/contracts/src/clientActivityCompat.ts for why there is no answer.
 */
export const WsServerReportClientActivityRpc = Rpc.make(WS_METHODS.serverReportClientActivity, {
  payload: ClientActivityReportInput,
});

export const WsServerDiscoverSourceControlRpc = Rpc.make(WS_METHODS.serverDiscoverSourceControl, {
  payload: Schema.Struct({}),
  success: SourceControlDiscoveryResult,
});

export const WsProjectsListEntriesRpc = Rpc.make(WS_METHODS.projectsListEntries, {
  payload: ProjectListEntriesInput,
  success: ProjectListEntriesResult,
  error: ProjectListEntriesError,
});

export const WsSubscribeTerminalMetadataRpc = Rpc.make(WS_METHODS.subscribeTerminalMetadata, {
  payload: Schema.Struct({}),
  success: TerminalMetadataStreamEvent,
  stream: true,
});

export const WsSubscribeBrowserEventsRpc = Rpc.make(WS_METHODS.subscribeBrowserEvents, {
  payload: BrowserSubscribeInput,
  success: BrowserStreamEvent,
  error: BrowserError,
  stream: true,
});

export const WsSubscribeServerConfigRpc = Rpc.make(WS_METHODS.subscribeServerConfig, {
  payload: Schema.Struct({}),
  success: ServerConfigStreamEvent,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError]),
  stream: true,
});

export const WsSubscribeServerLifecycleRpc = Rpc.make(WS_METHODS.subscribeServerLifecycle, {
  payload: Schema.Struct({}),
  success: ServerLifecycleStreamEvent,
  stream: true,
});

export const WsSubscribeAuthAccessRpc = Rpc.make(WS_METHODS.subscribeAuthAccess, {
  payload: Schema.Struct({}),
  success: AuthAccessStreamEvent,
  stream: true,
});

export const WsSubscribeWorkspaceReadinessRpc = Rpc.make(WS_METHODS.subscribeWorkspaceReadiness, {
  payload: WorkspaceReadinessSubscribeInput,
  success: PhaseEvent,
  error: WorkspaceReadinessError,
  stream: true,
});

/**
 * Per-environment usage scan. Every connected environment answers the same
 * window; the client merges the answers and de-duplicates shared transcript
 * directories. See `packages/contracts/src/usage.ts`.
 */
export const WsUsageGetSummaryRpc = Rpc.make(WS_METHODS.usageGetSummary, {
  payload: UsageSummaryInput,
  success: UsageSummary,
  error: UsageReadError,
});

/**
 * Where to fetch an attachment or a project favicon from.
 *
 * See `packages/contracts/src/assets.ts`. The client cannot build these
 * addresses itself: an attachment id says nothing about the route that serves
 * it, and both routes want a credential the client's image loader does not
 * send, which is what the returned URL carries.
 */
export const WsAssetsCreateUrlRpc = Rpc.make(WS_METHODS.assetsCreateUrl, {
  payload: AssetsRpcSchemas.createUrl.input,
  success: AssetsRpcSchemas.createUrl.output,
  error: AssetAccessError,
});

export const WsAttachmentsCreateUploadUrlRpc = Rpc.make(WS_METHODS.attachmentsCreateUploadUrl, {
  payload: AttachmentCreateUploadUrlInput,
  success: AttachmentCreateUploadUrlResult,
  error: AttachmentUploadSigningKeyError,
});

export const WsAttachmentsDeleteRpc = Rpc.make(WS_METHODS.attachmentsDelete, {
  payload: AttachmentDeleteInput,
  success: Schema.Void,
});

/** `/feedback` from inside a thread. See `packages/contracts/src/provider.ts`. */
export const WsProviderUploadFeedbackRpc = Rpc.make(WS_METHODS.providerUploadFeedback, {
  payload: ProviderUploadFeedbackInput,
  success: ProviderUploadFeedbackResult,
  error: ProviderUploadFeedbackError,
});

export const WsRpcGroup = RpcGroup.make(
  WsServerGetConfigRpc,
  WsServerRefreshProvidersRpc,
  WsServerStartProviderAuthRpc,
  WsServerRepairProviderAuthRuntimeRpc,
  WsServerDisconnectProviderAuthRpc,
  WsServerGetProviderAuthSessionRpc,
  WsServerCancelProviderAuthRpc,
  WsServerSubmitProviderAuthCodeRpc,
  WsServerSyncPersonalCodexToCloudRpc,
  WsServerSyncPersonalClaudeToCloudRpc,
  WsServerUpsertKeybindingRpc,
  WsServerRemoveKeybindingRpc,
  WsServerGetSettingsRpc,
  WsServerUpdateSettingsRpc,
  WsServerUpdateEnvironmentDisplayNameRpc,
  WsServerTraceDiagnosticsRpc,
  WsServerHeartbeatRpc,
  WsUsageGetSummaryRpc,
  WsPullRequestsListRpc,
  WsPullRequestsListStatsRpc,
  WsPullRequestsDetailRpc,
  WsPullRequestsActivityRpc,
  WsPullRequestsThreadCommentsRpc,
  WsPullRequestsDiffFileContentsRpc,
  WsPullRequestsRunActionRpc,
  WsPullRequestsUpdateRpc,
  WsPullRequestsCommentRpc,
  WsPullRequestsUpdateCommentRpc,
  WsPullRequestsSubmitReviewRpc,
  WsPullRequestsReplyToThreadRpc,
  WsPullRequestsSetThreadResolutionRpc,
  WsPullRequestsSetReactionRpc,
  WsPullRequestsInvalidateRpc,
  WsPullRequestsReviewerCandidatesRpc,
  WsPullRequestsRequestReviewersRpc,
  WsProjectsSearchContentsRpc,
  WsProjectsSearchEntriesRpc,
  WsProjectsWriteFileRpc,
  WsProjectCheckoutDeletionPreviewRpc,
  WsProjectCheckoutDeletionExecuteRpc,
  WsWorkspaceReadFileRpc,
  WsWorkspaceListDirectoryRpc,
  WsWorkspaceBeginWriteFileRpc,
  WsWorkspaceWriteFileChunkRpc,
  WsWorkspaceCommitWriteFileRpc,
  WsWorkspaceCancelWriteFileRpc,
  WsShellOpenInEditorRpc,
  WsSubscribeGitStatusRpc,
  WsGitPullRpc,
  WsGitRefreshStatusRpc,
  WsGitRunStackedActionRpc,
  WsGitResolvePullRequestRpc,
  WsGitPreparePullRequestThreadRpc,
  WsGitListBranchesRpc,
  WsVcsListRefsRpc,
  WsVcsRefreshStatusRpc,
  WsSubscribeVcsStatusRpc,
  WsVcsPullRpc,
  WsVcsCreateWorktreeRpc,
  WsVcsRemoveWorktreeRpc,
  WsVcsCreateRefRpc,
  WsVcsSwitchRefRpc,
  WsVcsInitRpc,
  WsGitCreateWorktreeRpc,
  WsGitRemoveWorktreeRpc,
  WsGitCreateBranchRpc,
  WsGitCheckoutRpc,
  WsGitInitRpc,
  WsFilesystemBrowseRpc,
  WsProjectsReadFileRpc,
  WsReviewGetDiffPreviewRpc,
  WsCloudListRuntimesRpc,
  WsTerminalOpenRpc,
  WsTerminalAttachRpc,
  WsTerminalWriteRpc,
  WsTerminalResizeRpc,
  WsTerminalSignalRpc,
  WsTerminalClearRpc,
  WsTerminalRestartRpc,
  WsTerminalCloseRpc,
  WsBrowserOpenSessionRpc,
  WsBrowserListSessionsRpc,
  WsBrowserCloseSessionRpc,
  WsBrowserGetSessionRpc,
  WsBrowserCommandRpc,
  WsBrowserClearBrowsingDataRpc,
  WsBrowserAttachDisplayRpc,
  WsBrowserAddDisplayIceCandidateRpc,
  WsComputerInputRpc,
  WsDevTunnelRegisterRpc,
  WsDevTunnelUnregisterRpc,
  WsDevTunnelListRpc,
  WsThreadDevRuntimeGetRpc,
  WsThreadDevRuntimeAllocatePortRpc,
  WsThreadDevRuntimeReleaseRpc,
  WsWorkspaceEnvironmentPreflightRpc,
  WsWorkspaceEnsureScratchRpc,
  WsSubscribeTerminalEventsRpc,
  WsSubscribeTerminalMetadataRpc,
  WsProjectsListEntriesRpc,
  WsServerDiscoverSourceControlRpc,
  WsServerReportClientActivityRpc,
  WsSubscribeBrowserEventsRpc,
  WsSubscribeServerConfigRpc,
  WsSubscribeServerLifecycleRpc,
  WsSubscribeAuthAccessRpc,
  WsSubscribeWorkspaceReadinessRpc,
  WsOrchestrationDispatchCommandRpc,
  WsOrchestrationGetTurnDiffRpc,
  WsOrchestrationGetFullThreadDiffRpc,
  WsOrchestrationStreamTurnDiffRpc,
  WsOrchestrationStreamFullThreadDiffRpc,
  WsOrchestrationReplayEventsRpc,
  WsOrchestrationGetArchivedShellSnapshotRpc,
  WsOrchestrationSubscribeShellRpc,
  WsOrchestrationSubscribeThreadRpc,
  WsAssetsCreateUrlRpc,
  WsAttachmentsCreateUploadUrlRpc,
  WsAttachmentsDeleteRpc,
  WsProviderUploadFeedbackRpc,
  WsOrchestrationSearchThreadsRpc,
  WsSourceControlLookupRepositoryRpc,
  WsSourceControlCloneRepositoryRpc,
  WsOrchestrationGetThreadDetailPageRpc,
);

export const RelayWsRpcGroup = WsRpcGroup.middleware(RelayAuthorizationMiddleware).middleware(
  RelayDeliveryMiddleware,
);

/**
 * The direct-WebSocket mount of the same group, with per-method scope
 * authorization attached.
 *
 * Kept separate from `WsRpcGroup` so clients keep generating against the plain
 * group — the middleware is a server-side gate, not a wire change — and
 * separate from `RelayWsRpcGroup` because the two lanes refuse with different
 * errors and derive their scopes from different places: a session role here, a
 * relay grant there.
 */
export const SessionWsRpcGroup = WsRpcGroup.middleware(SessionAuthorizationMiddleware);
